// Inline markup that renders literally until it closes, then reflows the line.
const PAIRED_MARKERS = ['**', '`'] as const

const BARE_MARKER_LINE = /^[\s\d#>*+\-=_.)]+$/u

function countOccurrences(text: string, marker: string): number {
  return text.split(marker).length - 1
}

/**
 * The part of a still-streaming answer that won't change shape when the next
 * token lands: any unclosed bold, inline code or link in the last line is
 * held back until it completes. Text inside an open code fence is left alone;
 * the code block handles its own streaming.
 */
export function trimToStableStreamingText(text: string): string {
  if (countOccurrences(text, '```') % 2 === 1) return text

  let stable = text
  const lineStart = stable.lastIndexOf('\n') + 1

  for (const marker of PAIRED_MARKERS) {
    const line = stable.slice(lineStart)
    if (countOccurrences(line, marker) % 2 === 1) {
      stable = stable.slice(0, lineStart + line.lastIndexOf(marker))
    }
  }

  const lastLine = stable.slice(lineStart)
  const linkStart = lastLine.lastIndexOf('[')
  if (linkStart !== -1 && !/^\[[^\]]*\]\([^)]*\)/u.test(lastLine.slice(linkStart))) {
    stable = stable.slice(0, lineStart + linkStart)
  }

  // A line of bare markers ("-", "1.", "#", ">") reshapes what's around it
  // until its text arrives: under a paragraph "-" turns it into a heading, an
  // empty item draws a lone bullet, and "2" joins the item above it.
  if (BARE_MARKER_LINE.test(stable.slice(lineStart))) stable = stable.slice(0, lineStart)

  return holdBackUnformedTable(stable).trimEnd()
}

/**
 * A table renders as a paragraph of pipes until its separator row lands, then
 * snaps into a grid. Hold its rows back until it has a header and separator.
 */
function holdBackUnformedTable(text: string): string {
  const lines = text.split('\n')
  let firstRow = lines.length
  while (firstRow > 0 && lines[firstRow - 1].trimStart().startsWith('|')) firstRow--
  if (firstRow === lines.length) return text
  const separator = lines[firstRow + 1]
  const isFormed =
    separator !== undefined &&
    /^[\s|:-]*-[\s|:-]*$/u.test(separator) &&
    countCells(separator) === countCells(lines[firstRow])
  return isFormed ? text : lines.slice(0, firstRow).join('\n')
}

function countCells(row: string): number {
  return row.trim().replace(/^\|/u, '').replace(/\|$/u, '').split('|').length
}
